<template>
    <div class="annual-plan-management">
        <TargetPageHeader
            title="연도별 이행계획"
            subtitle="전략 목표의 연도별 이행 계획을 설정하고 관리할 때 사용합니다."
        />

        <div v-if="loading && goalList.length === 0" class="loading-state">
            <div class="spinner"></div>
            <p>로딩 중...</p>
        </div>

        <div v-else-if="goalList.length === 0" class="empty-state">
            <p>등록된 목표가 없습니다.</p>
        </div>

        <div v-else class="annual-content">
            <!-- 목표 선택 영역 (카드형) -->
            <div class="goal-cards-section">
                <div class="goal-cards-container">
                    <div
                        v-for="goal in goalList"
                        :key="goal.targetIdx"
                        :class="['goal-card', { selected: selectedTargetIdx === goal.targetIdx }]"
                        @click="selectGoal(goal.targetIdx)"
                    >
                        <div class="goal-card-title-row">
                            <div class="goal-card-title">{{ goal.goalName }}</div>
                            <span
                                v-if="selectedTargetIdx === goal.targetIdx"
                                class="goal-card-check"
                                aria-hidden="true"
                            >
                                <i class="fa fa-check-circle"></i>
                            </span>
                        </div>
                        <div class="goal-card-badges">
                            <span class="badge badge-status">{{ getGoalStatusLabel(goal.goalStat) }}</span>
                            <span class="badge badge-reduct">{{ goal.reductRate }}%</span>
                        </div>
                        <div class="goal-card-data">
                            <div class="data-column data-column-left">
                                <div class="data-year">{{ goal.baseYear }}</div>
                                <div class="data-emission">
                                    {{ formatNumber(goal.baseEmissions) }}
                                    <span class="data-unit">{{ emissionUnit }}</span>
                                </div>
                            </div>
                            <div class="data-separator"></div>
                            <div class="data-column data-column-right">
                                <div class="data-year">{{ goal.targetYear }}</div>
                                <div class="data-emission">
                                    {{ formatNumber(goal.targetEmissions) }}
                                    <span class="data-unit">{{ emissionUnit }}</span>
                                </div>
                            </div>
                        </div>
                        <div class="goal-card-footer">
                            <div class="goal-card-date">생성 {{ formatShortDate(goal.regDate) }}</div>
                            <div class="goal-card-date">수정 {{ formatShortDate(goal.modDate) }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="!selectedTargetIdx" class="select-prompt">
                <div class="prompt-icon">
                    <i class="fa fa-mouse-pointer"></i>
                </div>
                <p class="prompt-text">목표를 선택하여 연도별 이행계획을 확인하세요</p>
            </div>

            <div v-else class="goal-content">
                <!-- 세부 감축 이행 계획 테이블 -->
                <div class="plan-table-section">
                    <div class="plan-table-header">
                        <h4>세부 감축 이행 계획</h4>
                        <div class="plan-table-actions">
                            <button class="btn-add" @click="openAddModal">
                                <i class="fa fa-plus"></i> 연도별 이행계획 추가
                            </button>
                        </div>
                    </div>

                    <div class="table-container">
                        <table class="plan-table">
                            <thead>
                                <tr>
                                    <th>이행계획 ID</th>
                                    <th>연도</th>
                                    <th>목표 배출량 (kgCO2e)</th>
                                    <th>실제 배출량 (kgCO2e)</th>
                                    <th>목표달성률(%)</th>
                                    <th>차입예약 (tCO₂e)</th>
                                    <th>상태</th>
                                    <th>등록일</th>
                                    <th>수정일</th>
                                    <th>담당자</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody v-if="skeletonVisible">
                                <tr v-for="n in 5" :key="'sk-' + n" class="skeleton-row">
                                    <td><span class="skeleton-block" style="width: 48px"></span></td>
                                    <td><span class="skeleton-block" style="width: 36px"></span></td>
                                    <td><span class="skeleton-block" style="width: 48px"></span></td>
                                    <td><span class="skeleton-block" style="width: 56px"></span></td>
                                    <td><span class="skeleton-block" style="width: 56px"></span></td>
                                    <td><span class="skeleton-block" style="width: 64px"></span></td>
                                    <td><span class="skeleton-block" style="width: 40px"></span></td>
                                    <td><span class="skeleton-block" style="width: 72px"></span></td>
                                    <td><span class="skeleton-block" style="width: 72px"></span></td>
                                    <td><span class="skeleton-block" style="width: 48px"></span></td>
                                    <td><span class="skeleton-block" style="width: 64px"></span></td>
                                </tr>
                            </tbody>
                            <tbody v-else-if="pathwaysData.length === 0">
                                <tr>
                                    <td colspan="12" class="text-center empty">등록된 이행계획이 없습니다.</td>
                                </tr>
                            </tbody>
                            <tbody v-else>
                                <tr v-for="pathway in sortedPathways" :key="pathway.pathwayIdx">
                                    <td class="id-cell">{{ pathway.pathwayIdx }}</td>
                                    <td class="year-cell">{{ pathway.pathwayYear }}</td>

                                    <td class="target-cell">
                                        {{ formatPathwayEmission(pathway.targetEmissions) }}
                                    </td>
                                    <td class="actual-cell">
                                        {{ formatPathwayEmission(pathway.actualEmissions) }}
                                    </td>
                                    <td class="progress-cell">
                                        <div class="progress-wrapper">
                                            <div class="progress-bar-container">
                                                <div
                                                    class="progress-bar-fill"
                                                    :style="{ width: Math.min(pathway.progressRate || 0, 100) + '%' }"
                                                ></div>
                                            </div>
                                            <span class="progress-text">{{ pathway.progressRate || 0 }}%</span>
                                        </div>
                                    </td>
                                    <td class="borrow-cell">
                                        {{
                                            pathway.borrowReserveKg > 0
                                                ? formatPathwayEmission(pathway.borrowReserveKg / 1000)
                                                : "—"
                                        }}
                                    </td>
                                    <td class="status-cell">
                                        <span :class="['status-badge', `status-${pathway.goalStat}`]">
                                            {{ getStatusLabel(pathway.goalStat) }}
                                        </span>
                                    </td>
                                    <td class="date-cell">{{ formatShortDate(pathway.regDate) }}</td>
                                    <td class="date-cell">{{ formatShortDate(pathway.modDate) }}</td>
                                    <td class="worker-cell">{{ pathway.worker || "-" }}</td>
                                    <td class="management-cell">
                                        <div class="action-buttons">
                                            <IconButton
                                                icon="fas fa-edit"
                                                title="수정"
                                                @click="openEditModal(pathway)"
                                            />
                                            <IconButton
                                                icon="fas fa-trash"
                                                variant="delete"
                                                title="삭제"
                                                @click="deletePlan(pathway)"
                                            />
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>

        <!-- 추가/수정 모달 -->
        <b-modal
            v-model="showModal"
            :title="isEditMode ? '이행계획 수정' : '이행계획 추가'"
            size="lg"
            @hidden="resetForm"
        >
            <div class="modal-form">
                <div class="form-row">
                    <div class="form-group half">
                        <label>연도 (YYYY) <span class="required">*</span></label>
                        <input
                            v-model="form.pathwayYear"
                            type="text"
                            class="form-control"
                            placeholder="2024"
                            maxlength="4"
                        />
                    </div>
                    <div class="form-group half">
                        <label>기간 구분</label>
                        <select v-model="form.periodType" class="form-control">
                            <option v-for="opt in periodTypeOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group half">
                        <label>목표 배출량 ({{ emissionUnit }}) <span class="required">*</span></label>
                        <input
                            v-model.number="form.targetEmissions"
                            type="number"
                            class="form-control"
                            placeholder="예: 17678517"
                            min="0"
                            step="1"
                        />
                        <small class="form-unit-hint">인벤토리·대시보드와 동일(kg). 톤(t)은 ×1000</small>
                    </div>
                    <div class="form-group half">
                        <label>실제 배출량 ({{ emissionUnit }})</label>
                        <input
                            v-model.number="form.actualEmissions"
                            type="number"
                            class="form-control"
                            placeholder="미입력 시 달성률 미계산"
                            min="0"
                            step="1"
                        />
                        <small class="form-unit-hint">확정 인벤토리 연간 합(kg). 대시보드 실적은 인벤토리 집계</small>
                    </div>
                </div>

                <div v-if="form.periodType !== 'YEAR' && form.periodType !== 'CUSTOM'" class="form-row">
                    <div class="form-group half">
                        <label>기간 값</label>
                        <input
                            v-model.number="form.periodValue"
                            type="number"
                            class="form-control"
                            placeholder="분기 1–4, 반기 1–2, 월 1–12"
                            min="1"
                            max="12"
                        />
                    </div>
                </div>

                <div v-if="form.periodType === 'CUSTOM'" class="form-row">
                    <div class="form-group half">
                        <label>시작일 <span class="required">*</span></label>
                        <input v-model="form.startDate" type="date" class="form-control" />
                    </div>
                    <div class="form-group half">
                        <label>종료일 <span class="required">*</span></label>
                        <input v-model="form.endDate" type="date" class="form-control" />
                    </div>
                </div>

                <div class="form-row">
                    <div class="form-group half">
                        <label>상태</label>
                        <select v-model="form.goalStat" class="form-control">
                            <option v-for="opt in goalStatOptions" :key="opt.value" :value="opt.value">
                                {{ opt.text }}
                            </option>
                        </select>
                    </div>
                    <div class="form-group half">
                        <label>비고</label>
                        <input v-model="form.remarks" type="text" class="form-control" placeholder="비고 (선택)" />
                    </div>
                </div>
            </div>

            <template #modal-footer>
                <button class="btn btn-secondary" @click="showModal = false">취소</button>
                <button class="btn btn-primary" @click="savePlan" :disabled="saving">
                    <span v-if="saving"><i class="fa fa-spinner fa-spin"></i> 저장 중...</span>
                    <span v-else>저장</span>
                </button>
            </template>
        </b-modal>
    </div>
</template>

<script>
import CarbonCodes from "../../../codes/carbonCodes";
import * as targetService from "../services/targetService";
import { TARGET_STORAGE_UNIT, formatTargetEmissionValue } from "../targetUnits";
import IconButton from "../../common/IconButton.vue";
import TargetPageHeader from "./TargetPageHeader.vue";

export default {
    name: "AnnualPlanManagement",
    components: { IconButton, TargetPageHeader },
    props: {
        isActive: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            goalList: [],
            selectedTargetIdx: null,
            pathwaysData: [],
            loading: false,
            loadingPathways: false,
            skeletonVisible: false,
            skeletonStart: 0,
            skeletonTimer: null,
            showModal: false,
            isEditMode: false,
            saving: false,
            chartInstance: null,
            form: {
                pathwayIdx: null,
                pathwayYear: new Date().getFullYear().toString(),
                targetEmissions: null,
                actualEmissions: null,
                periodType: "YEAR",
                periodValue: null,
                startDate: null,
                endDate: null,
                goalStat: "DRAFT",
                remarks: "",
            },
        };
    },
    computed: {
        periodTypeOptions() {
            return CarbonCodes.periodType.getList();
        },
        goalStatOptions() {
            return CarbonCodes.goalStat.getList();
        },
        sortedPathways() {
            return [...this.pathwaysData].sort((a, b) => {
                const yearA = parseInt(a.pathwayYear) || 0;
                const yearB = parseInt(b.pathwayYear) || 0;
                return yearA - yearB;
            });
        },
        emissionUnit() {
            return TARGET_STORAGE_UNIT;
        },
    },
    watch: {
        isActive: {
            immediate: true,
            handler(val) {
                if (val && this.goalList.length === 0) {
                    this.loadGoals();
                }
            },
        },
    },
    beforeDestroy() {
        if (this.chartInstance) {
            this.chartInstance.dispose();
        }
        if (this.skeletonTimer) {
            clearTimeout(this.skeletonTimer);
        }
    },
    methods: {
        async loadGoals() {
            this.loading = true;
            try {
                this.goalList = await targetService.fetchGoals();
            } catch (error) {
                console.error("목표 목록 조회 실패:", error);
                this.$bvToast.toast(error.message || "목표 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loading = false;
            }
        },
        selectGoal(targetIdx) {
            this.selectedTargetIdx = targetIdx;
            this.loadPathways();
        },
        getGoalStatusLabel(status) {
            return CarbonCodes.goalStat.getLabel(status) || (status ? status.toUpperCase() : "-");
        },

        formatShortDate(dateStr) {
            if (!dateStr) return "-";
            const date = new Date(dateStr);
            return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, "0")}.${String(date.getDate()).padStart(2, "0")}`;
        },
        getStatusLabel(status) {
            return CarbonCodes.goalStat.getLabel(status) || status;
        },
        async loadPathways() {
            if (!this.selectedTargetIdx) return;

            if (this.skeletonTimer) {
                clearTimeout(this.skeletonTimer);
                this.skeletonTimer = null;
            }
            this.skeletonVisible = true;
            this.skeletonStart = Date.now();
            this.loadingPathways = true;
            try {
                const response = await targetService.fetchAnnualPlans(this.selectedTargetIdx);
                const responseData = response?.data ?? response;
                const data = responseData?.data ?? responseData;
                this.pathwaysData = data?.pathways || (Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("이행계획 목록 조회 실패:", error);
                this.$bvToast.toast(error.message || "이행계획 목록을 불러오는데 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.loadingPathways = false;
                const elapsed = Date.now() - this.skeletonStart;
                const remaining = Math.max(0, 500 - elapsed);
                if (remaining > 0) {
                    this.skeletonTimer = setTimeout(() => {
                        this.skeletonVisible = false;
                        this.skeletonTimer = null;
                    }, remaining);
                } else {
                    this.skeletonVisible = false;
                }
            }
        },
        openAddModal() {
            if (!this.selectedTargetIdx) {
                this.$bvToast.toast("먼저 목표를 선택하세요.", { variant: "warning", title: "알림" });
                return;
            }
            this.isEditMode = false;
            this.resetForm();
            this.showModal = true;
        },
        openEditModal(pathway) {
            this.isEditMode = true;
            this.form = {
                pathwayIdx: pathway.pathwayIdx,
                pathwayYear: pathway.pathwayYear,
                targetEmissions: parseFloat(pathway.targetEmissions) || null,
                actualEmissions: pathway.actualEmissions != null ? parseFloat(pathway.actualEmissions) : null,
                periodType: pathway.periodType || "YEAR",
                periodValue: pathway.periodValue != null ? pathway.periodValue : null,
                startDate: pathway.startDate ? String(pathway.startDate).slice(0, 10) : null,
                endDate: pathway.endDate ? String(pathway.endDate).slice(0, 10) : null,
                goalStat: pathway.goalStat || "DRAFT",
                remarks: pathway.remarks || "",
            };
            this.showModal = true;
        },
        async savePlan() {
            if (!this.validateForm()) {
                return;
            }

            this.saving = true;
            try {
                const opt = (v) => (v !== undefined && v !== null && v !== "" ? v : null);
                const te = Number(this.form.targetEmissions);
                const payload = {
                    pathwayYear: String(this.form.pathwayYear).trim(),
                    targetEmissions: Number.isNaN(te) ? 0 : te,
                    actualEmissions: opt(this.form.actualEmissions),
                    periodType: this.form.periodType,
                    periodValue:
                        this.form.periodType !== "YEAR" && this.form.periodType !== "CUSTOM"
                            ? opt(this.form.periodValue)
                            : null,
                    startDate: this.form.periodType === "CUSTOM" ? this.form.startDate : null,
                    endDate: this.form.periodType === "CUSTOM" ? this.form.endDate : null,
                    goalStat: this.form.goalStat,
                    remarks: opt(this.form.remarks) || null,
                };

                if (this.isEditMode) {
                    let result = await targetService.updateAnnualPlan(
                        this.selectedTargetIdx,
                        this.form.pathwayIdx,
                        payload,
                    );
                    this.$bvToast.toast(result.data.message, {
                        variant: "success",
                        title: "저장 완료",
                    });
                } else {
                    const createPayload = { ...payload, targetIdx: this.selectedTargetIdx };
                    let result = await targetService.createAnnualPlan(this.selectedTargetIdx, createPayload);
                    this.$bvToast.toast(result.data.message, {
                        variant: "success",
                        title: "저장 완료",
                    });
                }

                this.showModal = false;
                await this.loadPathways();
            } catch (error) {
                console.error("이행계획 저장 실패:", error);
                this.$bvToast.toast(error.message || "이행계획 저장에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            } finally {
                this.saving = false;
            }
        },
        async deletePlan(pathway) {
            const confirmed = await this.alertConfirmWarning(`${pathway.pathwayYear}년 이행계획을 삭제하시겠습니까?`);

            if (!confirmed.value) return;

            try {
                let result = await targetService.deleteAnnualPlan(this.selectedTargetIdx, pathway.pathwayIdx);
                this.$bvToast.toast(result.data.data, {
                    variant: "success",
                    title: "삭제 완료",
                });
                await this.loadPathways();
            } catch (error) {
                console.error("이행계획 삭제 실패:", error);
                this.$bvToast.toast(error.message || "이행계획 삭제에 실패했습니다.", {
                    variant: "danger",
                    title: "오류",
                });
            }
        },
        validateForm() {
            const y = String(this.form.pathwayYear || "").trim();
            if (!y || !/^\d{4}$/.test(y)) {
                this.$bvToast.toast("연도는 YYYY 형식(4자리)으로 입력하세요.", {
                    variant: "warning",
                    title: "입력 오류",
                });
                return false;
            }
            const te = Number(this.form.targetEmissions);
            if (this.form.targetEmissions == null || this.form.targetEmissions === "" || Number.isNaN(te) || te < 0) {
                this.$bvToast.toast("목표 배출량을 0 이상으로 입력하세요.", { variant: "warning", title: "입력 오류" });
                return false;
            }
            if (this.form.periodType === "CUSTOM") {
                if (!this.form.startDate || !this.form.endDate) {
                    this.$bvToast.toast("기간 구분이 사용자정의일 때 시작일·종료일을 입력하세요.", {
                        variant: "warning",
                        title: "입력 오류",
                    });
                    return false;
                }
            }
            return true;
        },
        resetForm() {
            this.form = {
                pathwayIdx: null,
                pathwayYear: new Date().getFullYear().toString(),
                targetEmissions: null,
                actualEmissions: null,
                periodType: "YEAR",
                periodValue: null,
                startDate: null,
                endDate: null,
                goalStat: "DRAFT",
                remarks: "",
            };
        },
        formatNumber(num) {
            if (!num && num !== 0) return "0";
            return Number(num).toLocaleString("ko-KR", { maximumFractionDigits: 2 });
        },
        formatPathwayEmission(value) {
            return formatTargetEmissionValue(value, this.formatNumber);
        },
    },
};
</script>

<style scoped>
.annual-plan-management {
    padding: 16px;
    background: #f8f9fa;
    height: 100%;
    display: flex;
    flex-direction: column;
    border-radius: 12px;
}

.loading-state,
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px;
    color: #6b7280;
    background: white;
    border-radius: 8px;
}

.loading-state .spinner {
    margin-bottom: 12px;
}

.annual-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    min-height: 0;
}

/* 목표 선택 영역 (카드형) */
.goal-cards-section {
}

.goal-cards-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
}

.goal-cards-header h4 {
    font-size: 13px;
    font-weight: 600;
    color: #374151;
    margin: 0;
}

.goal-cards-container {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
}

.goal-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    transition:
        border-color 0.15s,
        background 0.15s,
        box-shadow 0.15s;
    min-height: 0;
    font-size: 12px;
}

.goal-card:hover {
    border-color: #94a3b8;
}

.goal-card.selected {
    border-color: #52b788;
    background: rgba(209, 250, 229, 0.35);
    box-shadow: 0 0 0 1px rgba(82, 183, 136, 0.2);
}

.goal-card-title-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 6px;
}

.goal-card-title {
    flex: 1;
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
    line-height: 1.35;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
}

.goal-card-check {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #059669;
    font-size: 14px;
    flex-shrink: 0;
    margin-top: 2px;
}

.goal-card-badges {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
}

.badge {
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.badge-status {
    background: #f3f4f6;
    color: #6b7280;
}

.badge-reduct {
    background: #d1fae5;
    color: #059669;
}

.goal-card-data {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 8px;
}

.data-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
}

.data-column-left {
    align-items: center;
}

.data-column-right {
    align-items: center;
    padding: 8px;
    background: #d1fae5;
    border-radius: 6px;
}

.data-separator {
    width: 1px;
    background: #e5e7eb;
    margin: 0 8px;
    flex-shrink: 0;
}

.data-year {
    font-size: 12px;
    font-weight: 700;
    color: #1f2937;
}

.data-column-right .data-year {
    color: #059669;
}

.data-emission {
    font-size: 11px;
    font-weight: 600;
    color: #4b5563;
}

.data-column-right .data-emission {
    color: #047857;
}

.data-unit {
    font-size: 10px;
    font-weight: 400;
    color: #9ca3af;
    margin-left: 2px;
}

.goal-card-footer {
    border-top: 1px solid #e5e7eb;
    padding-top: 8px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-top: 4px;
}

.goal-card-date {
    font-size: 10px;
    color: #9ca3af;
    font-weight: 500;
}

.select-prompt {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 95px 24px;
    background: #f9fafb;
    /* border: 1px solid #d1d5db; */
    border-radius: 8px;
    min-height: 200px;
}

.prompt-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e5e7eb;
    border-radius: 50%;
    margin-bottom: 16px;
    color: #9ca3af;
    font-size: 20px;
}

.prompt-text {
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    margin: 0;
}

.goal-content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;
    min-height: 0;
}

/* 테이블 섹션 */
.plan-table-section {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 0;
}

.plan-table-header {
    padding: 12px 16px;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
}

.plan-table-header h4 {
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
}

.plan-table-actions {
    display: flex;
    gap: 8px;
}

.btn-add {
    padding: 8px 15px;
    border: none;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    background: #52b788;
    color: white;
}

.btn-add:hover {
    background: #40916c;
    transform: translateY(-1px);
}

.btn-add i {
    margin-right: 6px;
}

.table-container {
    overflow-y: auto;
    overflow-x: auto;
    flex: 1;
    min-height: 0;
    max-height: 253px;
}

.plan-table {
    width: 100%;
    border-collapse: collapse;
}

.plan-table thead {
    background: #f9fafb;
    position: sticky;
    top: 0;
    z-index: 10;
}

.plan-table th {
    padding: 10px 12px;
    text-align: center;
    font-size: 11px;
    font-weight: 600;
    color: #374151;
    border-bottom: 1px solid #e5e7eb;
    white-space: nowrap;
}

.plan-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #f3f4f6;
    font-size: 12px;
    color: #4b5563;
    text-align: center;
}

.plan-table tbody tr:hover {
    background: #f9fafb;
}

.skeleton-row td {
    vertical-align: middle;
}

.skeleton-row:hover {
    background: transparent;
}

.skeleton-block {
    display: inline-block;
    height: 14px;
    background: linear-gradient(90deg, #e5e7eb 0%, #f3f4f6 50%, #e5e7eb 100%);
    background-size: 200% 100%;
    animation: skeleton-shimmer 1.2s ease-in-out infinite;
    border-radius: 4px;
}

@keyframes skeleton-shimmer {
    0% {
        background-position: 200% 0;
    }
    100% {
        background-position: -200% 0;
    }
}

.id-cell {
    font-size: 12px;
    color: #6b7280;
    text-align: center;
}

.year-cell {
    font-weight: 700;
    color: #1f2937;
    font-size: 13px;
}

.period-cell {
    font-size: 11px;
    color: #6b7280;
}

.target-cell,
.actual-cell {
    font-weight: 600;
    color: #1f2937;
    font-size: 12px;
    text-align: right;
}

.actual-cell {
    color: #3b82f6;
}

.progress-cell {
    padding: 8px 0;
}

.progress-wrapper {
    display: flex;
    align-items: center;
}

.progress-bar-container {
    flex: 1;
    height: 18px;
    background: #f3f4f6;
    border-radius: 4px;
    position: relative;
}

.progress-bar-fill {
    height: 100%;
    background: #10b981;
    border-radius: 4px;
    transition: width 0.3s;
}

.progress-text {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    min-width: 40px;
    text-align: right;
    margin-left: 12px;
}

.status-cell {
    text-align: center;
}

.status-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
}

.status-badge.status-draft {
    background: #f3f4f6;
    color: #6b7280;
}

.status-badge.status-active {
    background: #d1fae5;
    color: #065f46;
}

.status-badge.status-achieved {
    background: #fef3c7;
    color: #92400e;
}

.status-badge.status-failed {
    background: #fee2e2;
    color: #991b1b;
}

.status-badge.status-abandoned {
    background: #f3f4f6;
    color: #9ca3af;
}

.date-cell {
    font-size: 10px;
    color: #9ca3af;
}

.worker-cell {
    font-size: 11px;
    color: #6b7280;
}

.management-cell {
    width: 90px;
}

.action-buttons {
    display: flex;
    gap: 8px;
    justify-content: center;
}

.btn-action {
    width: 24px;
    height: 24px;
    padding: 0;
    border: none;
    border-radius: 0;
    font-size: 13px;
    cursor: pointer;
    transition: opacity 0.15s;
    background: transparent;
    color: #9ca3af;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-action:hover {
    opacity: 0.7;
    color: #374151;
}

.btn-action.btn-edit:hover {
    color: #047857;
}

.btn-action.btn-delete:hover {
    color: #ef4444;
}

.text-center {
    text-align: center;
}

.empty {
    padding: 32px 16px;
    color: #9ca3af;
    font-size: 12px;
}

.spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid #f3f4f6;
    border-top-color: #52b788;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin-right: 6px;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* 모달 폼 스타일 */
.modal-form {
    padding: 8px 0;
}

.form-row {
    display: flex;
    gap: 12px;
}

.form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.form-group.half {
    flex: 0 0 calc(50% - 6px);
}

.form-unit-hint {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    color: #6b7280;
    line-height: 1.4;
}

.form-group label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
}

.required {
    color: #ef4444;
}

.form-control {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 12px;
    transition: border-color 0.2s;
}

.form-control:focus {
    outline: none;
    border-color: #52b788;
    box-shadow: 0 0 0 3px rgba(82, 183, 136, 0.1);
}

.btn {
    padding: 8px 16px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.btn-secondary {
    background: #f3f4f6;
    color: #4b5563;
}

.btn-secondary:hover {
    background: #e5e7eb;
}

.btn-primary {
    background: #52b788;
    color: white;
}

.btn-primary:hover {
    background: #40916c;
}

.btn-primary:disabled {
    background: #9ca3af;
    cursor: not-allowed;
}
</style>
